import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useScopedTranslation from 'hooks/useScopedTranslation';
import CardStats from 'components/CardStats/CardStats';
import {
  listarPaneles,
  obtenerUltimoPanel,
  obtenerPanelPorHash,
  entiendeVersion,
} from 'services/panelAutopsiaService';
import { autopsiaI18n } from './i18n';
import { exportarPanelAExcel } from './exportarAutopsia';
import TablaCompliance from './partial/TablaCompliance';
import TablaCasos from './partial/TablaCasos';
import TablaProtocolo from './partial/TablaProtocolo';
import ChecklistConsultores from './partial/ChecklistConsultores';
import './autopsia.css';

/**
 * El export a Excel queda oculto: sale con la estructura de las tablas pero sin
 * el formato de la pantalla, porque la version community de xlsx no escribe
 * estilos. Poner en true para volver a mostrarlo.
 */
const MOSTRAR_EXPORTAR_EXCEL = false;

const Autopsia = ({ DesignSystemComponents }) => {
  const { Card, ComboBox, Loader, Alert, Button, Tabs } =
    DesignSystemComponents;
  const { t } = useScopedTranslation(autopsiaI18n);

  const [corridas, setCorridas] = useState([]);
  const [hashActivo, setHashActivo] = useState(null);
  const [panel, setPanel] = useState(null);
  const [meta, setMeta] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [exportando, setExportando] = useState(false);

  const descargarExcel = useCallback(() => {
    if (!panel) return;
    setExportando(true);
    try {
      exportarPanelAExcel(panel, meta, t);
    } catch (e) {
      console.error('Error al exportar el panel a Excel:', e);
      setError(t('ap_exportError'));
    } finally {
      setExportando(false);
    }
  }, [panel, meta, t]);

  // El listado alimenta el selector; el panel abre siempre en el ultimo generado.
  useEffect(() => {
    let vigente = true;

    const cargarInicial = async () => {
      setCargando(true);
      setError(null);
      try {
        const [lista, ultimo] = await Promise.all([
          listarPaneles(60).catch(() => []),
          obtenerUltimoPanel(),
        ]);
        if (!vigente) return;
        setCorridas(lista);
        setPanel(ultimo.panel);
        setMeta(ultimo.meta);
        setHashActivo(ultimo.meta?.hash ?? null);
      } catch (e) {
        if (!vigente) return;
        setError(
          e?.response?.status === 404 ? t('ap_empty') : t('ap_errorLoad')
        );
      } finally {
        if (vigente) setCargando(false);
      }
    };

    cargarInicial();
    return () => {
      vigente = false;
    };
  }, [t]);

  const cambiarCorrida = useCallback(
    async (hash) => {
      if (!hash || hash === hashActivo) return;
      setCargando(true);
      setError(null);
      try {
        const { panel: datos, meta: metadatos } = await obtenerPanelPorHash(
          hash
        );
        setPanel(datos);
        setMeta(metadatos);
        setHashActivo(hash);
      } catch (e) {
        setError(
          e?.response?.status === 502
            ? t('ap_errorStorage')
            : t('ap_errorLoad')
        );
      } finally {
        setCargando(false);
      }
    },
    [hashActivo, t]
  );

  const opciones = useMemo(
    () =>
      corridas.map((c) => ({
        value: c.hash,
        label: t('ap_runLabel', {
          fecha: c.fechaPanel ? String(c.fechaPanel).slice(0, 10) : '',
          negocios: c.totalNegocios ?? 0,
        }),
      })),
    [corridas, t]
  );

  const corridaActiva = useMemo(
    () => corridas.find((c) => c.hash === hashActivo) ?? null,
    [corridas, hashActivo]
  );

  const versionDesconocida =
    meta?.schemaVersion && !entiendeVersion(meta.schemaVersion);

  const enc = panel?.encabezado;
  const secciones = panel
    ? [
        {
          id: 'compliance',
          label: t('ap_secCompliance'),
          contenido: (
            <>
              {panel.compliance?.hallazgo_transversal && (
                <Alert variant='danger'>
                  {panel.compliance.hallazgo_transversal}
                </Alert>
              )}
              <p className='ap-nota'>
                {t('ap_sampleNote', {
                  citas: panel.compliance?.evidencia_textual?.length ?? 0,
                  casos:
                    (panel.compliance?.casos_criticos_total ?? 0) +
                    (panel.compliance?.casos_segundo_nivel_total ?? 0),
                })}
              </p>
              <TablaCompliance

                compliance={panel.compliance}
                anexo={panel.anexo_a_casos_criticos}
                t={t}
              />
            </>
          ),
        },
        {
          id: 'urgentes',
          label: t('ap_secUrgent'),
          contenido: (
            <TablaCasos

              casos={panel.recuperables_urgentes?.casos}
              etiquetaSenal={t('ap_colWindow')}
              mostrarLimite
              t={t}
            />
          ),
        },
        {
          id: 'protocolo',
          label: t('ap_secProtocol'),
          contenido: (
            <TablaProtocolo

              correcciones={panel.correcciones_protocolo}
              t={t}
            />
          ),
        },
        {
          id: 'checklist',
          label: t('ap_secChecklist'),
          contenido: (
            <ChecklistConsultores

              checklist={panel.checklist_diario}
              t={t}
            />
          ),
        },
      ]
    : [];

  return (
    <div className='ap-vista'>
      <div className='ap-cab'>
        <div>
          <h2>{t('ap_title')}</h2>
          <p className='mini'>{enc?.carga ?? t('ap_subtitle')}</p>
        </div>
        <div className='ap-acciones'>
          {opciones.length > 0 && (
            <div className='ap-selector'>
              <ComboBox
                label={t('ap_selectRun')}
                options={opciones}
                value={hashActivo}
                onChange={(v) => cambiarCorrida(v?.value ?? v)}
              />
            </div>
          )}
          {MOSTRAR_EXPORTAR_EXCEL && panel && !cargando && (
            <Button
              label={exportando ? t('ap_exporting') : t('ap_export')}
              onClick={descargarExcel}
              disabled={exportando}
              variant='secondary'
              iconStart='download'
            />
          )}
        </div>
      </div>

      {versionDesconocida && (
        <Alert variant='warning'>
          {t('ap_warnVersion', { version: meta.schemaVersion })}
        </Alert>
      )}
      {corridaActiva?.conNarrativa === false && (
        <Alert variant='info'>{t('ap_warnNoNarrative')}</Alert>
      )}
      {corridaActiva?.invariantesOk === false && (
        <Alert variant='warning'>{t('ap_warnInvariants')}</Alert>
      )}

      {error && (
        <Card className='ap-error'>
          <p>{error}</p>
          <Button label={t('ap_retry')} onClick={() => window.location.reload()} />
        </Card>
      )}

      {cargando && <Loader />}

      {!cargando && !error && panel && (
        <>
          <div className='ap-kpis'>
            <CardStats
              DesignSystemComponents={DesignSystemComponents}
              title={t('ap_kpiBusinesses')}
              count={corridaActiva?.totalNegocios ?? 0}
              type='info'
              icon='infoCircle'
              subtitle=''
              fill
            />
            <CardStats
              DesignSystemComponents={DesignSystemComponents}
              title={t('ap_kpiCompliance')}
              count={corridaActiva?.criticosCompliance ?? 0}
              type='error'
              icon='infoCircle'
              subtitle=''
              fill
            />
            <CardStats
              DesignSystemComponents={DesignSystemComponents}
              title={t('ap_kpiUrgent')}
              count={corridaActiva?.recuperablesUrgentes ?? 0}
              type='warning'
              icon='clock'
              subtitle=''
              fill
            />
            <CardStats
              DesignSystemComponents={DesignSystemComponents}
              title={t('ap_kpiConsultants')}
              count={corridaActiva?.consultores ?? 0}
              type='info'
              icon='infoCircle'
              subtitle=''
              fill
            />
          </div>

          {enc && (
            <Card className='ap-base'>
              <p className='mini'>
                <strong>{t('ap_window')}:</strong> {enc.ventana_cierre_desde} —{' '}
                {enc.ventana_cierre_hasta} · <strong>{t('ap_generatedAt')}:</strong>{' '}
                {panel.generado_en}
              </p>
              {enc.base && <p className='ap-meto'>{enc.base}</p>}
              {enc.criterio_priorizacion && (
                <p className='ap-meto'>{enc.criterio_priorizacion}</p>
              )}
            </Card>
          )}

          {/* El design system espera un render() por pestaña, no una lista de contenidos. */}
          <Tabs
            className='ap-tabs'
            tabs={secciones.map((s) => ({
              label: s.label,
              tabIdName: `ap-tab-${s.id}`,
              contentIdName: `ap-panel-${s.id}`,
              render: () => s.contenido,
            }))}
          />
        </>
      )}
    </div>
  );
};

export default Autopsia;
